import React, { useEffect, useLayoutEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useGallery } from '../context/GalleryContext';
import { Seo } from '../components/seo/Seo';
import { SITE_NAME } from '../constants';
import { Breadcrumb } from '../components/common/Breadcrumb';
import { GalleryStrip } from '../components/gallery/GalleryStrip';
import { GalleryList } from '../components/gallery/GalleryList';

// Remembers each section's window (vertical) scroll position for the mobile list, so returning
// via "back to gallery" restores it.
const savedWindowScroll = new Map<string, number>();

// A gallery section: breadcrumb + the section's works. Horizontal strip on desktop
// (≥ lg), single-column vertical list below lg; an empty-state when the section has no
// works yet. Both layouts render; CSS shows the right one so only the visible tiles'
// images load (lazy). Branch redirects happen in GalleryRouter.
export const GallerySectionPage = ({ slugPath }: { slugPath: string[] }) => {
    const { getMembership, getBreadcrumb } = useGallery();
    const location = useLocation();
    // "back to gallery" (from the viewing room) sets this so we restore the visitor's position.
    const restoreScroll = (location.state as { restoreScroll?: boolean } | null)?.restoreScroll === true;
    const works = getMembership(slugPath);
    const labels = getBreadcrumb(slugPath);
    const title = labels[labels.length - 1] ?? 'Gallery';
    const pathKey = slugPath.join('/');

    // Remember the window scroll position while browsing this section (mobile vertical list).
    useEffect(() => {
        const save = () => savedWindowScroll.set(pathKey, window.scrollY);
        window.addEventListener('scroll', save, { passive: true });
        return () => {
            save(); // capture the final position before leaving for the viewing room
            window.removeEventListener('scroll', save);
        };
    }, [pathKey]);

    // On mobile the vertical list scrolls the window; react-router keeps the scroll position
    // across route changes. Restore it when returning via "back to gallery"; otherwise start at
    // the top (navigating to a different section). Scrolling within the same section is preserved.
    useLayoutEffect(() => {
        window.scrollTo(0, restoreScroll ? savedWindowScroll.get(pathKey) ?? 0 : 0);
    }, [pathKey, restoreScroll]);

    return (
        <section className="flex h-full flex-col">
            <Seo
                title={labels.join(' — ')}
                description={`${title} — original artworks by ${SITE_NAME}.`}
                canonicalPath={`/${slugPath.join('/')}`}
            />
            <Breadcrumb path={slugPath} />
            <h1 className="sr-only">{title}</h1>

            <div className="mt-5 flex-1">
                {works.length === 0 ? (
                    <p className="text-meta italic text-tone-800">No works in this section yet.</p>
                ) : (
                    <>
                        {/* Strip ↔ list switches at lg, in lockstep with the sidebar ↔ hamburger nav. */}
                        <div className="hidden h-full lg:block">
                            <GalleryStrip artworks={works} sectionPath={slugPath} restoreScroll={restoreScroll} />
                        </div>
                        <div className="lg:hidden">
                            <GalleryList artworks={works} sectionPath={slugPath} />
                        </div>
                    </>
                )}
            </div>
        </section>
    );
};
